import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { Clock, CheckCircle, UtensilsCrossed, ArrowLeft, Bell, Package, XCircle, MessageCircle, MapPin, Navigation } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import OrderChatPanel from "@/components/OrderChatPanel";
import FunFactsCarousel from "@/components/FunFactsCarousel";
import pushService from "@/services/pushNotifications";
import { toast } from "sonner";

const STORE_LOGO_URL = "https://customer-assets.emergentagent.com/job_foodapp-24/artifacts/qn823x2x_PHOTO-2026-02-06-16-32-00.jpg";
const STORE_ADDRESS = "1000 Bienville Blvd, Dauphin Island, AL 36528";
const STORE_MAPS_URL = "https://maps.google.com/?q=1000+Bienville+Blvd+Dauphin+Island+AL+36528";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function OrderStatus() {
  const { orderId } = useParams();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showPushPrompt, setShowPushPrompt] = useState(false);
  const [pushEnabled, setPushEnabled] = useState(false);
  const [chatOpen, setChatOpen] = useState(false);
  const [unreadStaffMessages, setUnreadStaffMessages] = useState(0);

  useEffect(() => {
    let cancelled = false;
    const terminalStates = new Set(["ready", "completed", "picked_up", "declined"]);
    let interval;
    const fetchStatus = async () => {
      try {
        const res = await axios.get(`${API}/orders/status/${orderId}`);
        if (cancelled) return;
        setOrder(res.data);
        // Stop polling once the order reaches a terminal state
        if (terminalStates.has(res.data.status) && interval) {
          clearInterval(interval);
          interval = null;
        }
      } catch {
        if (!cancelled) setError("Order not found");
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    fetchStatus();
    interval = setInterval(fetchStatus, 5000);
    return () => { cancelled = true; if (interval) clearInterval(interval); };
  }, [orderId]);

  // Check push notification status on mount
  useEffect(() => {
    if (pushService.isSupported()) {
      setPushEnabled(pushService.isEnabled());
      if (!pushService.isEnabled() && !pushService.isDenied()) {
        setShowPushPrompt(true);
      }
    }
  }, []);

  const handleEnablePush = async () => {
    try {
      await pushService.subscribe();
      setPushEnabled(true);
      setShowPushPrompt(false);
      toast.success("Notifications enabled! You'll be notified when your order is ready.");
    } catch (e) {
      console.error('Push subscription error:', e);
      toast.error("Could not enable notifications.");
      setShowPushPrompt(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F8FAFC] flex items-center justify-center">
        <p className="text-slate-500 animate-pulse">Loading order status...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-[#F8FAFC] flex flex-col items-center justify-center p-6">
        <p className="text-slate-500 text-lg mb-4">{error}</p>
        <Link to="/order"><Button variant="outline">Back to Menu</Button></Link>
      </div>
    );
  }

  const isReady = order.status === "ready" || order.status === "completed";
  const isPickedUp = order.status === "picked_up";
  const isDeclined = order.status === "declined";
  const isFinal = isReady || isPickedUp || isDeclined;

  // Choose visual state for the status circle + text
  let circleBg = "bg-orange-100";
  let icon = <Clock className="h-16 w-16 text-orange-500 animate-pulse" />;
  let titleColor = "#F97316";
  let subtitle = "We're working on your order right now.";
  if (isPickedUp) {
    circleBg = "bg-emerald-100";
    icon = <Package className="h-16 w-16 text-emerald-600" />;
    titleColor = "#059669";
    subtitle = "Thanks for stopping by — enjoy your food!";
  } else if (isReady) {
    circleBg = "bg-green-100";
    icon = <CheckCircle className="h-16 w-16 text-green-600" />;
    titleColor = "#16A34A";
    subtitle = "Head to the counter to pick up your order!";
  } else if (isDeclined) {
    circleBg = "bg-red-100";
    icon = <XCircle className="h-16 w-16 text-red-600" />;
    titleColor = "#DC2626";
    subtitle = "The store was unable to accept your order.";
  }

  return (
    <div className="min-h-screen bg-[#F8FAFC] flex flex-col items-center justify-center p-6" data-testid="order-status-page">
      <div className="w-full max-w-sm text-center">
        {/* Store branding — Phase 15.4 */}
        <div className="flex flex-col items-center justify-center mb-6" data-testid="track-store-header">
          <img
            src={STORE_LOGO_URL}
            alt="Dauphin Island Food Mart"
            className="h-14 w-14 rounded-full object-cover shadow-md mb-2"
            data-testid="track-store-logo"
          />
          <span className="text-sm font-bold text-slate-800" style={{ fontFamily: "Manrope" }}>Dauphin Island Food Mart</span>
          <div className="flex items-center gap-1 mt-0.5 text-[11px] text-slate-500">
            <MapPin className="h-3 w-3" />
            <span>{STORE_ADDRESS}</span>
          </div>
        </div>

        {/* Status Indicator */}
        <div className={`w-32 h-32 rounded-full mx-auto mb-6 flex items-center justify-center ${circleBg}`}>
          {icon}
        </div>

        <h1
          className="text-3xl font-extrabold mb-2"
          style={{ fontFamily: "Manrope", color: titleColor }}
          data-testid="status-label"
        >
          {order.status_label}
        </h1>

        <p className="text-slate-500 text-sm mb-2">{subtitle}</p>

        {isDeclined && order.decline_reason && (
          <div className="mx-auto max-w-xs mb-4 rounded-xl p-3 border" style={{ backgroundColor: "#FEF2F2", borderColor: "#FECACA" }} data-testid="decline-reason">
            <p className="text-[10px] font-semibold uppercase tracking-wider mb-0.5" style={{ color: "#991B1B" }}>Reason</p>
            <p className="text-sm" style={{ color: "#7F1D1D" }}>{order.decline_reason}</p>
          </div>
        )}

        {/* Order Details */}
        <div className="bg-white rounded-2xl shadow-sm border border-slate-100 p-5 text-left space-y-3">
          <div className="flex justify-between">
            <span className="text-xs text-slate-500">Customer</span>
            <span className="text-sm font-semibold text-slate-900">{order.customer_name}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-xs text-slate-500">Order ID</span>
            <span className="text-sm font-mono text-slate-700">{order.id.slice(0, 8)}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-xs text-slate-500">Items</span>
            <span className="text-sm text-slate-700">{order.items_count} items</span>
          </div>
          <div className="flex justify-between">
            <span className="text-xs text-slate-500">Total</span>
            <span className="text-sm font-bold" style={{ color: "#FF6347" }}>${order.total.toFixed(2)}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-xs text-slate-500">Type</span>
            <Badge className="bg-orange-100 text-orange-700 border-orange-200 text-[10px]">TO-GO</Badge>
          </div>
        </div>

        <Link to="/order" className="block mt-6">
          <Button variant="outline" className="rounded-xl" data-testid="back-to-menu-btn">
            <ArrowLeft className="h-4 w-4 mr-1" /> Order More
          </Button>
        </Link>

        {/* Get Directions — Phase 15.4. Especially useful when the order is Ready. */}
        <a
          href={STORE_MAPS_URL}
          target="_blank"
          rel="noopener noreferrer"
          className="block mt-3"
          data-testid="get-directions-btn"
        >
          <Button
            className="w-full rounded-xl h-11 font-semibold text-white"
            style={{ backgroundColor: "#0EA5E9" }}
          >
            <Navigation className="h-4 w-4 mr-2" />
            Get Directions
          </Button>
        </a>

        {/* Fun Facts while waiting / preparing / ready — no need to distract them if they're picking up. */}
        {!isPickedUp && !isDeclined && (
          <div className="mt-4">
            <FunFactsCarousel intervalMs={6000} />
          </div>
        )}

        {/* Chat with Store */}
        {order.chat_open !== false && (
          <Button
            data-testid="open-chat-btn"
            className="w-full mt-3 rounded-xl h-11 font-semibold text-white relative"
            style={{ backgroundColor: "#FF6347" }}
            onClick={() => setChatOpen(true)}
          >
            <MessageCircle className="h-4 w-4 mr-2" />
            Chat with Store
            {unreadStaffMessages > 0 && (
              <span
                data-testid="unread-badge"
                className="absolute -top-1 -right-1 bg-yellow-400 text-slate-900 text-[10px] font-bold rounded-full h-5 min-w-5 px-1 flex items-center justify-center"
              >
                {unreadStaffMessages}
              </span>
            )}
          </Button>
        )}

        <Sheet open={chatOpen} onOpenChange={setChatOpen}>
          <SheetContent side="right" className="w-full sm:max-w-md p-0 flex flex-col">
            <SheetHeader className="px-4 py-3 border-b bg-white shrink-0">
              <SheetTitle className="text-base flex items-center gap-2">
                <MessageCircle className="h-4 w-4" style={{ color: "#FF6347" }} />
                Chat with the Store
              </SheetTitle>
              <p className="text-[11px] text-slate-500">Order #{order.id.slice(0, 8)}</p>
            </SheetHeader>
            <div className="flex-1 min-h-0">
              <OrderChatPanel
                orderId={order.id}
                viewer="customer"
                senderName={order.customer_name}
                onUnreadChange={setUnreadStaffMessages}
              />
            </div>
          </SheetContent>
        </Sheet>

        {/* Push Notification Prompt */}
        {showPushPrompt && !pushEnabled && (
          <div className="mt-4 p-4 bg-blue-50 rounded-2xl border border-blue-100">
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0">
                <Bell className="h-5 w-5 text-blue-600" />
              </div>
              <div className="flex-1">
                <p className="text-sm font-semibold text-blue-800">Get real-time updates!</p>
                <p className="text-xs text-blue-600 mt-1">Enable notifications to know when your order is ready.</p>
                <div className="flex gap-2 mt-3">
                  <Button 
                    size="sm" 
                    className="bg-blue-600 hover:bg-blue-700 text-white rounded-lg"
                    onClick={handleEnablePush}
                    data-testid="enable-push-btn"
                  >
                    Enable
                  </Button>
                  <Button 
                    size="sm" 
                    variant="ghost" 
                    className="text-blue-600"
                    onClick={() => setShowPushPrompt(false)}
                  >
                    Not now
                  </Button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Push Enabled Badge */}
        {pushEnabled && (
          <div className="mt-4 p-3 bg-green-50 rounded-xl flex items-center gap-2 justify-center">
            <Bell className="h-4 w-4 text-green-600" />
            <span className="text-xs font-medium text-green-700">Notifications enabled</span>
          </div>
        )}
      </div>
    </div>
  );
}
